import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { toolIntegrationService, type ToolIntegration } from '../../services/toolIntegration';

interface ConnectedToolsBarProps {
  onTaskSelect?: (task: string) => void;
  onSkillsActivate?: (skillIds: string[]) => void;
}

const ConnectedToolsBar: React.FC<ConnectedToolsBarProps> = ({ onTaskSelect, onSkillsActivate }) => {
  const [tools, setTools] = useState<ToolIntegration[]>([]);
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);

  useEffect(() => {
    toolIntegrationService.getEnabled().then(setTools);
  }, []);

  if (tools.length === 0) return null;

  const selectedTool = tools.find(t => t.id === selectedToolId);
  const hasQuickTasks = selectedTool && selectedTool.quickTasks && selectedTool.quickTasks.length > 0;

  const handleToolClick = (tool: ToolIntegration) => {
    if (!tool.quickTasks || tool.quickTasks.length === 0) return;
    const isDeselect = selectedToolId === tool.id;
    setSelectedToolId(isDeselect ? null : tool.id);
    onSkillsActivate?.(isDeselect ? [] : (tool.skills || []));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary text-center">
        {i18nService.t('toolIntegrationConnected')}
      </p>

      {/* Tool buttons — white floating card style */}
      <div className="flex items-center justify-center gap-2.5 flex-wrap">
        {tools.map(tool => {
          const hasQuick = tool.quickTasks && tool.quickTasks.length > 0;
          const isSelected = selectedToolId === tool.id;
          return (
            <button
              key={tool.id}
              type="button"
              onClick={() => handleToolClick(tool)}
              disabled={!hasQuick}
              className={`
                inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                text-sm font-medium transition-all duration-200 ease-out
                border shadow-sm
                ${isSelected
                  ? 'bg-white dark:bg-claude-darkSurface border-claude-accent/50 shadow-md text-claude-accent'
                  : hasQuick
                    ? 'bg-white dark:bg-claude-darkSurface border-claude-border dark:border-claude-darkBorder dark:text-claude-darkText text-claude-text hover:shadow-md hover:border-claude-accent/30 hover:-translate-y-0.5 cursor-pointer'
                    : 'bg-white dark:bg-claude-darkSurface border-claude-border dark:border-claude-darkBorder dark:text-claude-darkTextSecondary text-claude-textSecondary cursor-default opacity-60'
                }
              `}
            >
              {tool.logo ? (
                <img src={tool.logo} alt={tool.name} className="w-4 h-4 object-contain rounded" />
              ) : (
                <span className="text-sm">🔧</span>
              )}
              <span>{tool.name}</span>
              {hasQuick && (
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${
                    isSelected ? 'rotate-180 text-claude-accent' : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Quick tasks panel — slides in with fade-in-up */}
      {selectedTool && hasQuickTasks && (
        <div className="animate-fade-in-up origin-top">
          {/* Panel header */}
          <div className="mb-3 flex items-center gap-2 px-0.5">
            {selectedTool.logo ? (
              <img src={selectedTool.logo} alt={selectedTool.name} className="w-4 h-4 object-contain rounded" />
            ) : (
              <span className="text-xs">🔧</span>
            )}
            <span className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {selectedTool.name} · {i18nService.t('toolQuickTasksClickToUse')}
            </span>
          </div>

          {/* Quick task cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {selectedTool.quickTasks.map((task, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  onTaskSelect?.(task.prompt);
                  setSelectedToolId(null);
                }}
                style={{ animationDelay: `${idx * 30}ms` }}
                className="group animate-fade-in-up flex items-start justify-between gap-2 px-4 py-3 rounded-xl border text-left transition-all duration-200 ease-out bg-white dark:bg-claude-darkSurface border-claude-border dark:border-claude-darkBorder shadow-sm hover:shadow-md hover:border-claude-accent/30 hover:-translate-y-0.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium dark:text-claude-darkText text-claude-text">{task.title}</div>
                  {task.prompt !== task.title && (
                    <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-0.5 line-clamp-1">{task.prompt}</div>
                  )}
                </div>
                <ArrowRightIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 dark:text-claude-darkTextSecondary text-claude-textSecondary -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-200" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedToolsBar;
